import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Car, ChevronLeft, ChevronRight, Save, Trash2, SplitSquareVertical, X as XIcon, AlertTriangle, LayoutGrid, Rows3, Sun } from "lucide-react";
import PersonalVehicleReportDialog from "./PersonalVehicleReportDialog";
import { useConfirm } from "@/hooks/useConfirm";
import LabourReportPanel from "./LabourReport";
import { useLiveTick } from "@/hooks/useLiveTick";
import { useAuth } from "@/context/AuthContext";
import JobPicker from "@/components/dashboard/JobPicker";
import { PINNED_PROJECTS } from "@/components/dashboard/pinnedProjects";
import ClockLog from "@/components/dashboard/ClockLog";
import EmployeeDetailsDialog from "@/components/dashboard/EmployeeDetailsDialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/** Return the Monday of the week containing the given date (local time). */
function mondayOf(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}
function toISO(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
function fromISO(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function fmtRange(mon) {
  const opts = { month: "short", day: "numeric" };
  const start = mon.toLocaleDateString("en-US", opts);
  const end = addDays(mon, 6).toLocaleDateString("en-US", { ...opts, year: "numeric" });
  return `${start} – ${end}`.toUpperCase();
}
const emptyEntry = () => ({ hours: 0, project_id: "" });
const emptyDays = () => Array.from({ length: 7 }, () => [emptyEntry()]);

// Pinned pseudo-projects (Office / Estimating) come from the shared
// pinnedProjects module so every location dropdown on the site matches.

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Reasons for a zero-hours day. Keys are stable strings persisted to the DB
// (`day_reasons` field on the timesheet), values drive the styling + label.
const REASONS = {
  sick:         { label: "Sick",         short: "SICK",         color: "red",     tone: "border-red-500/50 bg-red-500/15 text-red-200"        },
  booked_off:   { label: "Booked Off",   short: "BOOKED OFF",   color: "orange",  tone: "border-orange-500/50 bg-orange-500/15 text-orange-200"},
  no_show:      { label: "No Show",      short: "NO SHOW",      color: "purple",  tone: "border-fuchsia-500/50 bg-fuchsia-500/15 text-fuchsia-200"},
  stat_holiday: { label: "Stat Holiday", short: "STAT HOLIDAY", color: "emerald", tone: "border-emerald-500/50 bg-emerald-500/15 text-emerald-200"},
  vacation:     { label: "Vacation",     short: "VACATION",     color: "blue",    tone: "border-sky-500/50 bg-sky-500/15 text-sky-200"        },
};
const REASON_KEYS = Object.keys(REASONS);

const HoursTab = () => {
  const { user } = useAuth();
  const [vehicleReportOpen, setVehicleReportOpen] = useState(false);
  const canSeeLabourByJob = ["owner", "superadmin", "admin"].includes(user?.role);
  const canSyncWeek = ["owner", "superadmin", "admin"].includes(user?.role);
  const storageKey = `hours-view-mode:${user?.user_id || "anon"}`;
  // View mode: "auto" (responsive), "grid" (full week table), "cards" (per-employee accordions).
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(storageKey) || "auto"; } catch { return "auto"; }
  });
  useEffect(() => {
    try {
      if (viewMode === "auto") localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, viewMode);
    } catch { /* ignore quota / private mode */ }
  }, [viewMode, storageKey]);
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const on = () => setIsNarrow(window.innerWidth < 1024);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const effectiveMode = viewMode === "auto" ? (isNarrow ? "cards" : "grid") : viewMode;

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [searchParams] = useSearchParams();
  const [flashCell, setFlashCell] = useState(null); // "empId-dayIdx" deep-linked from a location-flag notification
  // Clock rows arrive as minute-precision floats — display 2dp max, and
  // never mangle a value the user is mid-way through typing (strings pass).
  const fmtCellHours = (v) => {
    if (v == null || v === "") return "";
    if (typeof v === "number") return String(Math.round(v * 100) / 100);
    return String(v);
  };
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState({}); // employee_id -> days[7][entry...]
  const [awayDays, setAwayDays] = useState({}); // employee_id -> [bool*7]
  const [holidayDays, setHolidayDays] = useState({}); // employee_id -> [bool*7]
  // Per-day reason for zero-hours days. One of:
  //   "sick" (red) · "booked_off" (orange) · "no_show" (purple)
  //   · "stat_holiday" (emerald) · "vacation" (blue) · null (working)
  // Derived legacy flags (`away_days` / `holiday_days`) are still written to
  // the DB so the payroll calc keeps working unchanged.
  const [dayReasons, setDayReasons] = useState({}); // employee_id -> [reason|null]*7
  // Weekend days are locked by default; user taps "Worked" to enable input.
  // employee_id -> [bool*7] where index 5/6 (Sat/Sun) can be true.
  const [weekendWorked, setWeekendWorked] = useState({});
  const [holidayOverride, setHolidayOverride] = useState({}); // employee_id -> [number|null *7]
  const [statDays, setStatDays] = useState({}); // iso date -> Ontario ESA stat holiday name (auto-registered)
  const [statHours, setStatHours] = useState({}); // employee_id -> hours/day entitlement
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Deep link from a "didn't say where they worked" notification:
  // /dashboard?tab=hours&week=YYYY-MM-DD&emp=<id>&day=<0-6> opens that week
  // and flashes the red cell that needs a job.
  const flashDone = useRef(null); // one-shot guard — one scroll per deep link
  useEffect(() => {
    const w = searchParams.get("week");
    const emp = searchParams.get("emp");
    const day = searchParams.get("day");
    if (w && /^\d{4}-\d{2}-\d{2}$/.test(w)) setWeekStart(new Date(`${w}T12:00:00`));
    if (emp && day != null && day !== "") {
      flashDone.current = null; // fresh deep link — allow one new scroll
      setFlashCell(`${emp}-${day}`);
    }
  }, [searchParams]);
  useEffect(() => {
    if (!flashCell || loading) return;
    if (flashDone.current === flashCell) return; // already scrolled for this deep link
    // Lock IMMEDIATELY and keep `rows` OUT of the deps below: a keystroke in
    // any hours cell changes `rows`, and must never re-run or re-schedule
    // this scroll. Fires exactly once, 400ms after the data is ready.
    flashDone.current = flashCell;
    const t = setTimeout(() => {
      // End the red pulse highlight a few seconds later either way.
      setTimeout(() => setFlashCell(null), 3000);
      // If the user already clicked into a cell and started typing, do NOT
      // yank the view to the flagged cell — the pulse alone marks it.
      const active = document.activeElement;
      if (active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) return;
      const el = document.querySelector(`[data-testid^="hours-cell-${flashCell}-"], [data-testid="hours-mobile-day-${flashCell}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    return () => clearTimeout(t);
  }, [flashCell, loading]); // rows deliberately excluded — typing never re-triggers the scroll
  const [pastWeeks, setPastWeeks] = useState([]);
  const [detailsFor, setDetailsFor] = useState(null);
  // { empId, dayIdx, entryIdx } identifying which cell should receive the new
  // project id once created. null when the create dialog is closed.
  const [creatingFor, setCreatingFor] = useState(null);
  const [newProj, setNewProj] = useState({ name: "", location: "", description: "" });
  const [creatingBusy, setCreatingBusy] = useState(false);

  // labour-by-job report panel (shared with the Search page)
  const [reportOpen, setReportOpen] = useState(false);
  const confirm = useConfirm();

  const weekISO = useMemo(() => toISO(weekStart), [weekStart]);
  const rangeLabel = useMemo(() => fmtRange(weekStart), [weekStart]);

  // Cap forward navigation at "next week" — one work-week ahead of today's
  // Monday. Users can still go arbitrarily far back through the archive.
  const maxWeekISO = useMemo(() => toISO(addDays(mondayOf(new Date()), 7)), []);
  const canGoNext = weekISO < maxWeekISO;

  const activeProjects = useMemo(
    () => projects.filter((p) => p.status === "In Progress" || p.status === "Active"),
    [projects],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, projRes, tsRes, weeksRes, holRes] = await Promise.all([
        api.get("/employees"),
        api.get("/projects"),
        api.get(`/timesheets?week_start=${weekISO}`),
        api.get("/timesheets/weeks"),
        // Ontario ESA stat holidays for the visible week — these register
        // automatically (highlight + ESA stat pay) with no manual flagging.
        api.get(`/timesheets/stat-holidays?start=${weekISO}&end=${toISO(addDays(weekStart, 6))}`).catch(() => ({ data: { holidays: {} } })),
      ]);
      const weekHolidays = holRes.data?.holidays || {};
      setStatDays(weekHolidays);
      // Show anyone still Active OR whose inactive_since is AFTER the week
      // being viewed. That way historical weeks stay complete, but future
      // weeks drop deactivated employees automatically.
      const activeEmps = (empRes.data || []).filter((e) => {
        if ((e.status || "Active") === "Active") return true;
        const iso = (e.inactive_since || "").slice(0, 10);
        return iso && iso > weekISO;
      });
      setEmployees(activeEmps);
      setProjects(projRes.data || []);
      setPastWeeks(weeksRes.data || []);
      const map = {};
      const awayMap = {};
      const holidayMap = {};
      const holidayOvMap = {};
      const reasonMap = {};
      const weekendMap = {};
      (tsRes.data || []).forEach((t) => {
        map[t.employee_id] = Array.isArray(t.days) && t.days.length === 7
          ? t.days.map((day) => (Array.isArray(day) && day.length ? day : [emptyEntry()]))
          : emptyDays();
        const aw = Array.isArray(t.away_days) ? t.away_days : [];
        awayMap[t.employee_id] = Array.from({ length: 7 }, (_, i) => !!aw[i]);
        const hd = Array.isArray(t.holiday_days) ? t.holiday_days : [];
        holidayMap[t.employee_id] = Array.from({ length: 7 }, (_, i) => !!hd[i]);
        const ov = Array.isArray(t.holiday_hours_override) ? t.holiday_hours_override : [];
        holidayOvMap[t.employee_id] = Array.from({ length: 7 }, (_, i) => (ov[i] == null ? "" : String(ov[i])));
        const rs = Array.isArray(t.day_reasons) ? t.day_reasons : [];
        // Migrate legacy flags into the new per-day reason field so the UI is
        // consistent even for weeks saved before this dropdown existed.
        reasonMap[t.employee_id] = Array.from({ length: 7 }, (_, i) => {
          const explicit = rs[i];
          if (explicit && REASONS[explicit]) return explicit;
          if (hd[i]) return "stat_holiday";
          if (aw[i]) return "sick"; // legacy "Away/sick" defaulted to sick
          return null;
        });
        // Any weekend day that already has hours or a reason auto-unlocks so
        // the admin's existing weekend work stays editable.
        const dayRows = Array.isArray(t.days) ? t.days : [];
        weekendMap[t.employee_id] = Array.from({ length: 7 }, (_, i) => {
          if (i < 5) return false;
          const row = Array.isArray(dayRows[i]) ? dayRows[i] : [];
          const anyHours = row.some((r) => Number(r?.hours || 0) > 0);
          const hasReason = !!(rs[i] || hd[i] || aw[i]);
          return anyHours || hasReason;
        });
      });
      activeEmps.forEach((e) => {
        if (!map[e.employee_id]) map[e.employee_id] = emptyDays();
        if (!awayMap[e.employee_id]) awayMap[e.employee_id] = Array(7).fill(false);
        if (!holidayMap[e.employee_id]) holidayMap[e.employee_id] = Array(7).fill(false);
        if (!holidayOvMap[e.employee_id]) holidayOvMap[e.employee_id] = Array(7).fill("");
        if (!reasonMap[e.employee_id]) reasonMap[e.employee_id] = Array(7).fill(null);
        if (!weekendMap[e.employee_id]) weekendMap[e.employee_id] = Array(7).fill(false);
      });
      setRows(map);
      setAwayDays(awayMap);
      setHolidayDays(holidayMap);
      setHolidayOverride(holidayOvMap);
      setDayReasons(reasonMap);
      setWeekendWorked(weekendMap);
      // Pre-fetch ESA-computed stat hours for any employee with a stat day
      // marked so the weekly Total renders correctly on a fresh page load.
      // When the week itself contains a calendar stat holiday, EVERY hourly
      // employee is entitled (it auto-registers), so fetch for all of them.
      const calStatInWeek = Object.keys(weekHolidays).length > 0;
      const needsStatFetch = Object.keys(holidayMap).filter((eid) => calStatInWeek || holidayMap[eid].some(Boolean));
      if (needsStatFetch.length) {
        const results = await Promise.all(
          needsStatFetch.map((eid) =>
            api.get(`/timesheets/stat-holiday-hours?employee_id=${eid}&week_start=${weekISO}`)
              .then((r) => [eid, Number(r.data?.hours_per_day || 0)])
              .catch(() => [eid, 0]),
          ),
        );
        setStatHours((prev) => {
          const next = { ...prev };
          results.forEach(([eid, hrs]) => { next[eid] = hrs; });
          return next;
        });
      }
    } catch {
      toast.error("Could not load timesheets");
    } finally {
      setLoading(false);
    }
  }, [weekISO]);

  // Auto clock-sync: every time the viewed week changes, gently rebuild
  // clock-sourced rows from raw punches (manual edits are preserved by the
  // backend), THEN load the grid. Silent by design — the numbers simply
  // come back up to date. Guarded so React StrictMode / re-renders don't
  // fire it twice for the same week.
  const syncedWeekRef = useRef(null);
  useEffect(() => {
    if (syncedWeekRef.current === weekISO) return;
    syncedWeekRef.current = weekISO;
    (async () => {
      // sync-week is owner/admin/superadmin only on the server; managers skip it
      if (canSyncWeek) {
        try { await api.post("/timesheets/sync-week", { week_start: weekISO }); } catch { /* silent */ }
      }
      await load();
    })();
  }, [weekISO, load, canSyncWeek]);
  useLiveTick(load); // impersonation live-watch: refetch on every tick

  const updateEntry = (empId, dayIdx, entryIdx, field, raw) => {
    if (field === "project_id" && raw === "__new__") {
      setNewProj({ name: "", location: "", description: "" });
      setCreatingFor({ empId, dayIdx, entryIdx });
      return;
    }
    setRows((prev) => {
      const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
      if (!days[dayIdx][entryIdx]) days[dayIdx][entryIdx] = emptyEntry();
      const row = days[dayIdx][entryIdx];
      if (field === "hours" && row.source === "clock") {
        // Typing a custom hours value takes ownership of the row — drop the
        // clock tag so later punch syncs leave it alone.
        delete row.source;
        delete row.project_locked;
        delete row.clock_project_id;
      }
      if (field === "project_id" && String(raw || "").trim()) {
        // Assigning a job does NOT untie the row from the clock: the hours
        // keep tracking the punches (a punch-time edit must always flow
        // through). We stamp the punch's own project so every future sync
        // re-applies THIS curated job instead of resetting to the punch's.
        if (row.source === "clock" && !row.project_locked) {
          row.clock_project_id = row.project_id || "";
          row.project_locked = true;
        }
        delete row.needs_location;
        delete row.typed_location;
      }
      if (field === "project_id" && !String(raw || "").trim()) {
        // Clearing the job hands the row back to the punch's own project.
        delete row.project_locked;
        delete row.clock_project_id;
      }
      if (field === "hours") {
        // Keep the raw string (like "7." or "7.25") so the user can type
        // fractions without parseFloat stripping the trailing dot.
        let clean = String(raw).replace(/[^0-9.]/g, "");
        const parts = clean.split(".");
        if (parts.length > 2) clean = parts[0] + "." + parts.slice(1).join("");
        row.hours = clean;
      } else if (field === "project_id") {
        row.project_id = raw;
      }
      return { ...prev, [empId]: days };
    });
  };

  /** Toggle an employee's away/sick flag for a specific day of this week.
   * When True: hours for that day are zeroed AND the whole week loses the
   * "perfect attendance" bonus rate in payroll. */
  /** Set the reason for a day (or clear it). Any reason zeroes the day's
   * hours (like the legacy Away button did) and also updates the derived
   * legacy `awayDays` / `holidayDays` flags so the backend + payroll calc
   * keep working without changes. */
  const setReason = async (empId, dayIdx, reason) => {
    setDayReasons((prev) => {
      const arr = prev[empId] ? [...prev[empId]] : Array(7).fill(null);
      arr[dayIdx] = reason || null;
      return { ...prev, [empId]: arr };
    });
    const isHoliday = reason === "stat_holiday";
    const isOff = !!reason && !isHoliday;
    setAwayDays((prev) => {
      const arr = prev[empId] ? [...prev[empId]] : Array(7).fill(false);
      arr[dayIdx] = isOff;
      return { ...prev, [empId]: arr };
    });
    setHolidayDays((prev) => {
      const arr = prev[empId] ? [...prev[empId]] : Array(7).fill(false);
      arr[dayIdx] = isHoliday;
      return { ...prev, [empId]: arr };
    });
    // Zero hours whenever any reason is set (unpaid day off / stat pays via
    // the separate statHours channel — worked hours stay 0).
    if (reason) {
      setRows((prev) => {
        const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
        days[dayIdx] = days[dayIdx].map((e) => ({ ...e, hours: 0 }));
        return { ...prev, [empId]: days };
      });
    }
    // For stat holidays, pre-fetch the ESA-entitled hours for the row.
    if (isHoliday && statHours[empId] === undefined) {
      try {
        const { data } = await api.get(`/timesheets/stat-holiday-hours?employee_id=${empId}&week_start=${weekISO}`);
        setStatHours((prev) => ({ ...prev, [empId]: Number(data.hours_per_day || 0) }));
      } catch { /* silent */ }
    }
  };

  const toggleAway = (empId, dayIdx) => {
    setAwayDays((prev) => {
      const arr = (prev[empId] ? [...prev[empId]] : Array(7).fill(false));
      arr[dayIdx] = !arr[dayIdx];
      return { ...prev, [empId]: arr };
    });
    setRows((prev) => {
      const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
      const willBeAway = !(awayDays[empId]?.[dayIdx]);
      if (willBeAway) {
        days[dayIdx] = days[dayIdx].map((e) => ({ ...e, hours: 0 }));
      }
      return { ...prev, [empId]: days };
    });
  };

  /** Set the manual per-day override hours for a stat holiday day. */
  const setHolidayOv = (empId, dayIdx, val) => {
    let clean = String(val ?? "").replace(/[^0-9.]/g, "");
    const parts = clean.split(".");
    if (parts.length > 2) clean = parts[0] + "." + parts.slice(1).join("");
    setHolidayOverride((prev) => {
      const arr = prev[empId] ? [...prev[empId]] : Array(7).fill("");
      arr[dayIdx] = clean;
      return { ...prev, [empId]: arr };
    });
  };

  /** Toggle statutory holiday flag for a day. When on, we fetch the ESA
   * lookback so the UI can show the hours the employee is entitled to. */
  const toggleHoliday = async (empId, dayIdx) => {
    const willBeHoliday = !(holidayDays[empId]?.[dayIdx]);
    setHolidayDays((prev) => {
      const arr = (prev[empId] ? [...prev[empId]] : Array(7).fill(false));
      arr[dayIdx] = willBeHoliday;
      return { ...prev, [empId]: arr };
    });
    if (willBeHoliday) {
      // Zero the day's hours (worked-hours are separate from stat holiday pay).
      setRows((prev) => {
        const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
        days[dayIdx] = days[dayIdx].map((e) => ({ ...e, hours: 0 }));
        return { ...prev, [empId]: days };
      });
      // Cache the entitled hours per day if not already fetched.
      if (statHours[empId] === undefined) {
        try {
          const { data } = await api.get(`/timesheets/stat-holiday-hours?employee_id=${empId}&week_start=${weekISO}`);
          setStatHours((prev) => ({ ...prev, [empId]: Number(data.hours_per_day || 0) }));
        } catch { /* silent */ }
      }
    }
  };

  const assignWeek = (empId, projectId) => {
    if (!projectId) return;
    if (projectId === "__new__") {
      // Route through the create-project modal, then apply on save. Store the
      // "whole-week" hint by using entryIdx = -1.
      setNewProj({ name: "", location: "", description: "" });
      setCreatingFor({ empId, dayIdx: -1, entryIdx: -1 });
      return;
    }
    setRows((prev) => {
      const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
      for (let d = 0; d < 7; d++) {
        if (!days[d][0]) days[d][0] = emptyEntry();
        days[d][0].project_id = projectId;
      }
      return { ...prev, [empId]: days };
    });
  };

  /** Auto-fill 8 hours on Mon–Fri (weekdays 0..4). Leaves the weekend
   *  alone and only touches days that currently show zero hours so an
   *  admin's existing entries aren't clobbered. Reason flags block a day
   *  the same way they block manual editing. */
  const fillWeekdays = (empId) => {
    setRows((prev) => {
      const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
      const reasons = dayReasons[empId] || Array(7).fill(null);
      for (let d = 0; d < 5; d++) {
        if (reasons[d]) continue;
        if (statNameFor(d)) continue; // stat holiday — don't fill worked hours over it
        if (!days[d][0]) days[d][0] = emptyEntry();
        const cur = Number(days[d][0].hours || 0);
        if (cur === 0) days[d][0].hours = 8;
      }
      return { ...prev, [empId]: days };
    });
  };

  const toggleWeekendWorked = (empId, dayIdx) => {
    setWeekendWorked((prev) => {
      const arr = prev[empId] ? [...prev[empId]] : Array(7).fill(false);
      arr[dayIdx] = !arr[dayIdx];
      return { ...prev, [empId]: arr };
    });
  };

  const createProject = async () => {
    if (!newProj.name.trim()) {
      toast.error("Job name is required");
      return;
    }
    setCreatingBusy(true);
    try {
      const { data } = await api.post("/projects", {
        name: newProj.name.trim(),
        location: newProj.location.trim(),
        description: newProj.description.trim(),
        status: "Active",
        needs_review: true,
      });
      // Refresh projects list so the new one shows up in the dropdown
      const projRes = await api.get("/projects");
      setProjects(projRes.data || []);
      // Auto-select the new project in the originating cell (or apply to
      // the whole week if creatingFor came from "assign week").
      if (creatingFor && data?.project_id) {
        const { empId, dayIdx, entryIdx } = creatingFor;
        setRows((prev) => {
          const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
          if (dayIdx === -1) {
            for (let d = 0; d < 7; d++) {
              if (!days[d][0]) days[d][0] = emptyEntry();
              days[d][0].project_id = data.project_id;
            }
          } else {
            if (!days[dayIdx][entryIdx]) days[dayIdx][entryIdx] = emptyEntry();
            days[dayIdx][entryIdx].project_id = data.project_id;
          }
          return { ...prev, [empId]: days };
        });
      }
      toast.success("Job created — details still needed");
      setCreatingFor(null);
    } catch {
      toast.error("Could not create job");
    } finally {
      setCreatingBusy(false);
    }
  };

  const splitDay = (empId, dayIdx) => {
    setRows((prev) => {
      const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
      // No cap on entries — a tech can visit many sites in one day.
      days[dayIdx].push(emptyEntry());
      return { ...prev, [empId]: days };
    });
  };

  const removeSplit = (empId, dayIdx, entryIdx) => {
    // A Personal Vehicle (+2h) row is backed by the manager's logged entry —
    // removing it here deletes that entry too, otherwise the clock sync
    // would just put the 2 hours back.
    const victim = (rows[empId] || [])[dayIdx]?.[entryIdx];
    if (victim?.source === "personal_vehicle" && victim.vehicle_id) {
      api.delete(`/personal-vehicle/${victim.vehicle_id}`)
        .then(() => toast.success("Personal vehicle entry removed"))
        .catch(() => toast.error("Could not remove the personal vehicle entry"));
    }
    setRows((prev) => {
      const days = (prev[empId] || emptyDays()).map((d) => d.map((e) => ({ ...e })));
      if (days[dayIdx].length > 1) days[dayIdx].splice(entryIdx, 1);
      else if (victim?.source === "personal_vehicle") days[dayIdx] = [emptyEntry()];
      return { ...prev, [empId]: days };
    });
  };

  /** Ontario ESA stat holiday name for week day index d (0=Mon), or null. */
  const statNameFor = (d) => statDays[toISO(addDays(weekStart, d))] || null;

  const rowHours = (days, empId) => {
    const worked = (days || []).reduce(
      (sum, day) => sum + day.reduce((s, e) => s + (Number(e?.hours) || 0), 0),
      0,
    );
    // Day cells stay by the minute — ONLY the week total snaps to the
    // nearest 0.25h (ties up), matching what payroll pays.
    const quarterTotal = (x) => Math.floor(x * 4 + 0.5) / 4;
    // Add stat holiday hours (override if user set one, else ESA default).
    // Calendar stat holidays count automatically — same union payroll does.
    if (!empId) return quarterTotal(worked);
    const empRec = employees.find((x) => x.employee_id === empId);
    if ((empRec?.pay_type || "hourly") === "salary") return quarterTotal(worked);
    const hd = holidayDays[empId] || [];
    const eff = Array.from({ length: 7 }, (_, i) => !!hd[i] || !!statNameFor(i));
    if (!eff.some(Boolean)) return quarterTotal(worked);
    const ov = holidayOverride[empId] || [];
    const def = statHours[empId] || 0;
    let statTotal = 0;
    eff.forEach((flag, i) => {
      if (!flag) return;
      const v = ov[i];
      statTotal += (v !== "" && v != null) ? (parseFloat(v) || 0) : def;
    });
    return quarterTotal(worked + statTotal);
  };

  const saveSheet = async ({ advance }) => {
    setSaving(true);
    try {
      // Save every row in parallel.
      await Promise.all(
        employees.map((e) =>
          api.put(`/timesheets/${e.employee_id}`, {
            employee_id: e.employee_id,
            week_start: weekISO,
            // Coerce any raw-string hours into floats before sending.
            days: (rows[e.employee_id] || emptyDays()).map((day) =>
              day.map((en) => ({ ...en, hours: parseFloat(en?.hours) || 0 })),
            ),
            away_days: awayDays[e.employee_id] || Array(7).fill(false),
            holiday_days: holidayDays[e.employee_id] || Array(7).fill(false),
            day_reasons: dayReasons[e.employee_id] || Array(7).fill(null),
            holiday_hours_override: (holidayOverride[e.employee_id] || Array(7).fill("")).map(
              (v) => (v === "" || v === null || v === undefined) ? null : parseFloat(v),
            ),
            submitted: true,
          }),
        ),
      );
      toast.success(advance ? "Week saved. New sheet started." : "Week saved");
      if (advance) {
        const nextStart = addDays(weekStart, 7);
        if (toISO(nextStart) <= maxWeekISO) setWeekStart(nextStart);
      } else {
        // refresh weeks list
        try {
          const { data } = await api.get("/timesheets/weeks");
          setPastWeeks(data || []);
        } catch (e) { /* ignore */ }
      }
    } catch {
      toast.error("Could not save week");
    } finally {
      setSaving(false);
    }
  };

  /* -----------------------------------------------------------------------
   * Auto-save. Every mutation to rows / awayDays / holidayDays triggers a
   * debounced background PUT for just the affected employee. No global
   * spinner, no toast — a subtle status indicator in the header is enough.
   * --------------------------------------------------------------------- */
  const autoSaveTimers = useRef({});
  const [autoSaveState, setAutoSaveState] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const savedTimer = useRef(null);
  const persistOne = useCallback((empId) => {
    if (!empId || !weekISO) return;
    // Cancel any pending save for this employee — coalesce edits.
    if (autoSaveTimers.current[empId]) clearTimeout(autoSaveTimers.current[empId]);
    autoSaveTimers.current[empId] = setTimeout(async () => {
      setAutoSaveState("saving");
      try {
        await api.put(`/timesheets/${empId}`, {
          employee_id: empId,
          week_start: weekISO,
          days: (rows[empId] || emptyDays()).map((day) =>
            day.map((en) => ({ ...en, hours: parseFloat(en?.hours) || 0 })),
          ),
          away_days: awayDays[empId] || Array(7).fill(false),
          holiday_days: holidayDays[empId] || Array(7).fill(false),
          day_reasons: dayReasons[empId] || Array(7).fill(null),
          holiday_hours_override: (holidayOverride[empId] || Array(7).fill("")).map(
            (v) => (v === "" || v === null || v === undefined) ? null : parseFloat(v),
          ),
          submitted: false,
        });
        setAutoSaveState("saved");
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setAutoSaveState("idle"), 1600);
      } catch {
        setAutoSaveState("error");
      }
    }, 450);
  }, [rows, awayDays, holidayDays, holidayOverride, dayReasons, weekISO]);

  // Any time an employee's data changes, queue an auto-save for THAT employee.
  // We track a signature per employee so we don't blast every row on load.
  const lastSigRef = useRef({});
  useEffect(() => {
    if (loading) return;
    Object.keys(rows).forEach((empId) => {
      const sig = JSON.stringify({
        d: rows[empId],
        a: awayDays[empId],
        h: holidayDays[empId],
        o: holidayOverride[empId],
        r: dayReasons[empId],
      });
      if (lastSigRef.current[empId] !== sig) {
        // Skip the first snapshot after load (no real edit yet).
        if (lastSigRef.current[empId] !== undefined) persistOne(empId);
        lastSigRef.current[empId] = sig;
      }
    });
  }, [rows, awayDays, holidayDays, holidayOverride, dayReasons, weekISO, loading, persistOne]);

  // Reset per-week snapshot so switching weeks doesn't cross-save.
  useEffect(() => { lastSigRef.current = {}; }, [weekISO]);

  const deleteWeek = async () => {
    const ok = await confirm({
      title: `Delete week of ${rangeLabel}?`,
      description: "Every employee's hours + project assignments for this week will be removed. This can't be undone.",
      confirmLabel: "Delete week",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/timesheets/week/${weekISO}`);
      toast.success("Week deleted");
      await load();
    } catch {
      toast.error("Could not delete week");
    }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="hours-tab">
      <PersonalVehicleReportDialog open={vehicleReportOpen} onClose={() => setVehicleReportOpen(false)} />
      <ClockLog />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-start gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Timesheet</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pr-3">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">Employee Hours</h2>
              <span
                data-testid="hours-autosave-state"
                className={`hidden shrink-0 whitespace-nowrap font-mono text-[10px] uppercase tracking-widest sm:inline ${
                  autoSaveState === "saving" ? "text-[#8fa4f0]"
                  : autoSaveState === "saved" ? "text-emerald-400"
                  : autoSaveState === "error" ? "text-red-400"
                  : "text-white/30"
                }`}
              >
                {autoSaveState === "saving" ? "· saving…"
                  : autoSaveState === "saved" ? "· saved"
                  : autoSaveState === "error" ? "· save failed"
                  : "· auto-save on"}
              </span>
            </div>
          </div>
          {/* Monthly Personal Vehicle report (owner/admin/superadmin) */}
          {["owner", "admin", "superadmin"].includes(user?.role) && (
            <button
              type="button"
              onClick={() => setVehicleReportOpen(true)}
              data-testid="hours-vehicle-report-btn"
              title="Monthly Personal Vehicle report — destinations, purposes and +2h allowances per manager"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 px-2.5 py-2 text-[10px] font-semibold uppercase tracking-widest text-violet-700 hover:bg-violet-500/20 dark:text-violet-200"
            >
              <Car className="h-3.5 w-3.5" /> Vehicle report
            </button>
          )}
          {/* View toggle: Cards / Grid — remembered per-user in localStorage. */}
          <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-black/15 bg-black/[0.04] dark:border-white/15 dark:bg-white/5" data-testid="hours-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              data-testid="hours-view-cards"
              aria-pressed={effectiveMode === "cards"}
              title="Card view (one employee at a time)"
              className={`flex items-center gap-1.5 px-2.5 py-2 text-[10px] font-semibold uppercase tracking-widest transition ${
                effectiveMode === "cards"
                  ? "bg-[#1a3fd1] text-white"
                  : "text-slate-600 hover:text-slate-900 dark:text-white/60 dark:hover:text-white"
              }`}
            >
              <Rows3 className="h-3.5 w-3.5" /> Cards
            </button>
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              data-testid="hours-view-grid"
              aria-pressed={effectiveMode === "grid"}
              title="Grid view (full-week table)"
              className={`flex items-center gap-1.5 px-2.5 py-2 text-[10px] font-semibold uppercase tracking-widest transition ${
                effectiveMode === "grid"
                  ? "bg-[#1a3fd1] text-white"
                  : "text-slate-600 hover:text-slate-900 dark:text-white/60 dark:hover:text-white"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Grid
            </button>
          </div>
        </div>
        <div className="flex max-w-full flex-wrap items-center gap-2">
          <button
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            data-testid="hours-prev-week"
            className="rounded-md border border-white/15 bg-white/5 p-2 text-white/70 hover:border-white/25 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <select
            value={weekISO}
            onChange={(ev) => setWeekStart(mondayOf(fromISO(ev.target.value)))}
            data-testid="hours-range-label"
            title="Jump to another week"
            className="min-w-0 max-w-[calc(100vw-2rem)] flex-shrink truncate rounded-md border border-white/15 bg-white/5 px-4 py-2 font-mono text-xs tracking-widest text-white [color-scheme:dark] hover:border-white/25 focus:outline-none"
          >
            {(pastWeeks.includes(weekISO) ? pastWeeks : [weekISO, ...pastWeeks]).map((w) => (
              <option key={w} value={w}>
                {fmtRange(fromISO(w))}
              </option>
            ))}
          </select>
          <button
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            disabled={!canGoNext}
            data-testid="hours-next-week"
            aria-label="Next week"
            title={canGoNext ? "Next week" : "Can't scroll past next week"}
            className={`rounded-md border p-2 ${
              canGoNext
                ? "border-white/15 bg-white/5 text-white/70 hover:border-white/25 hover:text-white"
                : "cursor-not-allowed border-white/5 bg-white/[0.02] text-white/20"
            }`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => setWeekStart(mondayOf(new Date()))}
            data-testid="hours-this-week"
            className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white/70 hover:border-white/25 hover:text-white"
          >
            This week
          </button>
          {canSeeLabourByJob && (
            <button
              onClick={() => setReportOpen((v) => !v)}
              data-testid="labour-report-toggle"
              aria-expanded={reportOpen}
              className={`rounded-md border px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                reportOpen
                  ? "border-[#f97316] bg-[#f97316]/15 text-[#fb923c]"
                  : "border-white/15 bg-white/5 text-white/70 hover:border-white/25 hover:text-white"
              }`}
            >
              Labour by job
            </button>
          )}
        </div>
      </div>

      {reportOpen && canSeeLabourByJob && <LabourReportPanel />}

      {effectiveMode === "grid" && (
      <div className="overflow-x-auto rounded-lg border border-white/10" data-testid="hours-grid">
        <table className="w-full min-w-[1100px] border-collapse text-sm">
          <thead>
            <tr className="bg-[#1a3fd1] text-left text-white">
              <th className="w-44 px-3 py-2 font-semibold">Employee</th>
              {DAYS.map((d, i) => (
                <th key={d} className={`w-28 px-2 py-2 text-center font-semibold ${i >= 5 ? "bg-[#152fa8]" : ""}`}>
                  {d}
                  <div className="font-mono text-[9px] font-semibold" data-testid={`hours-grid-date-${i}`}>{addDays(weekStart, i).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                  {statNameFor(i) && (
                    <div
                      className="mt-0.5 font-mono text-[9px] font-semibold uppercase tracking-widest text-emerald-200"
                      data-testid={`hours-stat-name-${i}`}
                    >
                      {statNameFor(i)}
                    </div>
                  )}
                </th>
              ))}
              <th className="w-20 px-3 py-2 text-center font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-white/50">Loading…</td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-white/50">No active employees.</td>
              </tr>
            ) : (
              employees.map((e, i) => {
                const days = rows[e.employee_id] || emptyDays();
                const isSalary = e.pay_type === "salary";
                const hoursSum = rowHours(days, e.employee_id);
                const totalDisplay = hoursSum.toFixed(2);
                return (
                  <tr
                    key={e.employee_id}
                    data-testid={`hours-row-${e.employee_id}`}
                    className={i % 2 ? "bg-white/[0.02]" : ""}
                  >
                    <td className="px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => setDetailsFor(e)}
                        data-testid={`hours-employee-link-${e.employee_id}`}
                        className="text-left font-medium text-white/90 hover:text-[#8fa4f0] hover:underline"
                      >
                        {e.name}
                      </button>
                      {isSalary && (
                        <div className="mt-0.5 inline-block rounded bg-amber-500/20 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-widest text-amber-300">Salary</div>
                      )}
                      <div className="mt-1 space-y-1">
                        <JobPicker
                          value=""
                          onChange={(id) => assignWeek(e.employee_id, id)}
                          onCreate={() => setCreatingFor({ empId: e.employee_id, dayIdx: 0, entryIdx: 0 })}
                          pinned={PINNED_PROJECTS}
                          projects={activeProjects}
                          placeholder="Apply to week…"
                          className="h-6 text-[10px]"
                          testid={`hours-assign-week-${e.employee_id}`}
                          allowClear={false}
                        />
                        <button
                          type="button"
                          onClick={() => fillWeekdays(e.employee_id)}
                          data-testid={`hours-fill-40-${e.employee_id}`}
                          title="Fill 8 hours Mon–Fri (empty cells only)"
                          className="w-full rounded border border-emerald-500/50 bg-emerald-500/15 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-widest text-emerald-200 hover:border-emerald-400 hover:bg-emerald-500/25"
                        >
                          Fill 40h · M–F
                        </button>
                      </div>
                    </td>
                    {DAYS.map((_, d) => {
                      const away = !!(awayDays[e.employee_id]?.[d]);
                      const holiday = !!(holidayDays[e.employee_id]?.[d]);
                      const reason = dayReasons[e.employee_id]?.[d] || null;
                      const meta = reason ? REASONS[reason] : null;
                      const statHrs = statHours[e.employee_id];
                      const isWeekend = d >= 5;
                      const worked = !!(weekendWorked[e.employee_id]?.[d]);
                      const weekendLocked = isWeekend && !worked && !reason;
                      // Calendar stat holiday auto-registers: if nobody set a
                      // reason and no hours were worked that day, the cell is
                      // a stat day (ESA entitlement shows, editable below).
                      const calStat = statNameFor(d);
                      const dayWorkedHrs = (days[d] || []).reduce((s, en) => s + (Number(en?.hours) || 0), 0);
                      const autoStat = !!calStat && !reason && dayWorkedHrs === 0;
                      const effStat = reason === "stat_holiday" || autoStat;
                      return (
                      <td key={d} className={`border-l border-white/5 px-1 py-1 align-top ${
                        meta ? meta.tone.split(" ")[1] :
                        autoStat ? "bg-emerald-500/10" :
                        weekendLocked ? "bg-slate-500/[0.07]" :
                        isWeekend ? "bg-slate-500/[0.14]" : ""
                      }`}>
                        {weekendLocked ? (
                          <button
                            type="button"
                            onClick={() => toggleWeekendWorked(e.employee_id, d)}
                            data-testid={`hours-worked-${e.employee_id}-${d}`}
                            title="Enable this weekend day for hour entry"
                            className="flex h-14 w-full items-center justify-center rounded border border-dashed border-slate-500/60 bg-slate-500/10 font-mono text-[10px] font-semibold uppercase tracking-widest text-slate-600 hover:border-emerald-400 dark:text-slate-300 hover:bg-emerald-500/10 hover:text-emerald-200"
                          >
                            + Worked
                          </button>
                        ) : reason && reason !== "stat_holiday" ? (
                          <div
                            data-testid={`hours-reason-${reason}-${e.employee_id}-${d}`}
                            title={`${meta.label} — pick a different reason below`}
                            className={`flex h-14 w-full items-center justify-center rounded border font-mono text-[10px] uppercase tracking-widest ${meta.tone}`}
                          >
                            {meta.short}
                          </div>
                        ) : effStat ? (
                          <div className="flex flex-col items-stretch gap-1">
                            {calStat && (
                              <p className="text-center font-mono text-[9px] font-semibold uppercase tracking-widest text-emerald-300" data-testid={`hours-stat-label-${e.employee_id}-${d}`}>
                                {calStat}
                              </p>
                            )}
                            <input
                              type="text"
                              inputMode="decimal"
                              value={holidayOverride[e.employee_id]?.[d] ?? ""}
                              onChange={(ev) => setHolidayOv(e.employee_id, d, ev.target.value)}
                              placeholder={statHrs !== undefined ? statHrs.toFixed(2) : "0"}
                              data-testid={`hours-stat-hrs-${e.employee_id}-${d}`}
                              title="Stat hours (editable). Blank = ESA default."
                              className="h-8 w-full rounded border border-emerald-500/40 bg-emerald-500/10 px-1 text-center font-mono text-xs text-emerald-100 placeholder:text-emerald-300/40 focus:border-emerald-400 focus:outline-none"
                            />
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {days[d].map((entry, ei) => {
                              const fromClock = entry?.source === "clock";
                              const vehicle = entry?.source === "personal_vehicle";
                              const needsLoc = !!entry?.needs_location;
                              const typedLoc = (entry?.typed_location || "").trim();
                              const flash = flashCell === `${e.employee_id}-${d}`;
                              return (
                              <div
                                key={ei}
                                data-row-source={entry?.source || undefined}
                                className={`rounded border p-1 ${vehicle ? "border-violet-500/60 bg-violet-500/15" : needsLoc ? (typedLoc ? "border-orange-500 bg-orange-500/20" : "border-red-500 bg-red-500/20") : fromClock ? "border-yellow-500/50 bg-yellow-500/15" : "border-white/10 bg-white/[0.02]"} ${flash ? `animate-pulse ring-2 ${needsLoc && typedLoc ? "ring-orange-500" : "ring-red-500"}` : ""}`}
                                title={vehicle ? (entry?.note || "Personal vehicle allowance (+2h)") : needsLoc ? (typedLoc ? `Typed location "${typedLoc}" — pick the matching project below` : "No job location — pick one below") : fromClock ? "Auto-filled from the time clock" : ""}
                                data-locflag={needsLoc ? "true" : undefined}
                              >
                                <div className="relative">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={fmtCellHours(entry?.hours) === "0" ? "" : fmtCellHours(entry?.hours)}
                                    onChange={(ev) => updateEntry(e.employee_id, d, ei, "hours", ev.target.value)}
                                    data-testid={`hours-cell-${e.employee_id}-${d}-${ei}`}
                                    placeholder="hrs"
                                    className={`h-7 w-full rounded border border-transparent bg-transparent pl-2 pr-6 text-center hover:border-white/15 focus:border-[#1a3fd1] focus:bg-white/5 focus:outline-none ${fromClock ? "font-semibold text-yellow-900 dark:text-yellow-200" : "text-white/90"}`}
                                  />
                                  {ei === 0 ? (
                                      <button
                                        type="button"
                                        onClick={() => splitDay(e.employee_id, d)}
                                        data-testid={`hours-split-${e.employee_id}-${d}`}
                                        aria-label="Add another job site to this day"
                                        title="Add another job site to this day"
                                        className="absolute right-0.5 top-0.5 rounded p-0.5 text-white/30 hover:bg-white/5 hover:text-[#8fa4f0]"
                                      >
                                        <SplitSquareVertical className="h-3 w-3" />
                                      </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => removeSplit(e.employee_id, d, ei)}
                                      data-testid={`hours-remove-split-${e.employee_id}-${d}-${ei}`}
                                      aria-label="Remove split"
                                      title="Remove split"
                                      className="absolute right-0.5 top-0.5 rounded p-0.5 text-white/30 hover:bg-white/5 hover:text-red-400"
                                    >
                                      <XIcon className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                                {needsLoc && typedLoc && (
                                  <p className="mt-0.5 truncate px-0.5 text-center font-mono text-[9px] uppercase tracking-wide text-orange-300" title={typedLoc} data-testid={`typed-loc-${e.employee_id}-${d}-${ei}`}>{typedLoc}</p>
                                )}
                                <div className="mt-1">
                                  {vehicle ? (
                                    <div className="flex h-6 items-center justify-center rounded border border-violet-500/50 bg-violet-500/15 px-1 font-mono text-[10px] uppercase tracking-wider text-violet-200" data-testid={`hours-vehicle-label-${e.employee_id}-${d}-${ei}`}>Personal vehicle</div>
                                  ) : (
                                  <JobPicker
                                    value={entry?.project_id || ""}
                                    onChange={(id) => updateEntry(e.employee_id, d, ei, "project_id", id)}
                                    onCreate={() => updateEntry(e.employee_id, d, ei, "project_id", "__new__")}
                                    pinned={PINNED_PROJECTS}
                                    projects={activeProjects}
                                    placeholder="— job —"
                                    className="h-6 text-[10px]"
                                    testid={`hours-project-${e.employee_id}-${d}-${ei}`}
                                  />
                                  )}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Per-day reason dropdown — replaces old icon buttons. */}
                        <select
                          value={reason || (autoStat ? "stat_holiday" : "")}
                          onChange={(ev) => setReason(e.employee_id, d, ev.target.value || null)}
                          data-testid={`hours-reason-${e.employee_id}-${d}`}
                          className={`mt-1 h-6 w-full rounded border bg-white/5 px-1 font-mono text-[9.5px] focus:border-[#1a3fd1] focus:outline-none ${
                            meta ? "border-white/25 text-white" : "border-white/10 text-white/50"
                          }`}
                        >
                          <option value="" className="bg-[#0a0a0a] text-white">— Working —</option>
                          {REASON_KEYS.map((k) => (
                            <option key={k} value={k} className="bg-[#0a0a0a] text-white">{REASONS[k].label}</option>
                          ))}
                        </select>
                      </td>
                      );
                    })}
                    <td
                      className="border-l border-white/5 px-3 py-1.5 text-center font-mono text-white/70"
                      data-testid={`hours-total-${e.employee_id}`}
                    >
                      {totalDisplay}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Card layout — one collapsible card per employee */}
      {effectiveMode === "cards" && (
      <div className="space-y-2" data-testid="hours-mobile-cards">
        {loading ? (
          <p className="rounded-lg border border-white/10 p-6 text-center text-sm text-white/50">Loading…</p>
        ) : employees.length === 0 ? (
          <p className="rounded-lg border border-white/10 p-6 text-center text-sm text-white/50">No active employees.</p>
        ) : (
          employees.map((e) => {
            const days = rows[e.employee_id] || emptyDays();
            const isSalary = e.pay_type === "salary";
            const hoursSum = rowHours(days, e.employee_id);
            const totalDisplay = isSalary ? "Salary" : `${hoursSum.toFixed(2)} hrs`;
            return (
              <details
                key={e.employee_id}
                className="rounded-lg border border-white/10 bg-white/[0.02] open:bg-white/[0.04]"
                data-testid={`hours-mcard-${e.employee_id}`}
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 [&::-webkit-details-marker]:hidden">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/40 transition-transform group-open:rotate-90" />
                    <span className="truncate font-medium text-white/90">{e.name}</span>
                    {isSalary && (
                      <span className="rounded bg-amber-500/20 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-widest text-amber-300">Salary</span>
                    )}
                  </div>
                  <span className="shrink-0 font-mono text-sm text-white/80">{totalDisplay}</span>
                </summary>

                <div className="border-t border-white/5 p-3">
                  <div className="mb-3 flex flex-wrap items-stretch gap-2">
                    <div className="min-w-[10rem] flex-1">
                      <JobPicker
                        value=""
                        onChange={(id) => assignWeek(e.employee_id, id)}
                        onCreate={() => setCreatingFor({ empId: e.employee_id, dayIdx: 0, entryIdx: 0 })}
                        pinned={PINNED_PROJECTS}
                        projects={activeProjects}
                        placeholder="Apply job to whole week…"
                        className="h-8 text-xs"
                        testid={`hours-mobile-assign-${e.employee_id}`}
                        allowClear={false}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => fillWeekdays(e.employee_id)}
                      data-testid={`hours-mobile-fill-40-${e.employee_id}`}
                      title="Fill 8 hours Mon–Fri (empty cells only)"
                      className="whitespace-nowrap rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-200 hover:border-emerald-400 hover:bg-emerald-500/25"
                    >
                      Fill 40h · M–F
                    </button>
                  </div>
                  <div className="space-y-2">
                    {DAYS.map((label, d) => {
                      const away = !!(awayDays[e.employee_id]?.[d]);
                      const holiday = !!(holidayDays[e.employee_id]?.[d]);
                      const reason = dayReasons[e.employee_id]?.[d] || null;
                      const meta = reason ? REASONS[reason] : null;
                      const statHrs = statHours[e.employee_id];
                      const isWeekend = d >= 5;
                      const worked = !!(weekendWorked[e.employee_id]?.[d]);
                      const weekendLocked = isWeekend && !worked && !reason;
                      // Calendar stat holiday auto-registers (see grid view).
                      const calStat = statNameFor(d);
                      const dayWorkedHrs = (days[d] || []).reduce((s, en) => s + (Number(en?.hours) || 0), 0);
                      const autoStat = !!calStat && !reason && dayWorkedHrs === 0;
                      const effStat = reason === "stat_holiday" || autoStat;
                      return (
                      <div
                        key={d}
                        className={`rounded-md border px-2 py-2 ${
                          meta ? meta.tone :
                          autoStat ? "border-emerald-500/50 bg-emerald-500/15" :
                          weekendLocked ? "border-slate-600/40 bg-slate-500/[0.07]" :
                          isWeekend ? "border-slate-500/40 bg-slate-500/[0.12]" :
                          "border-white/5 bg-white/[0.02]"
                        }`}
                        data-testid={`hours-mobile-day-${e.employee_id}-${d}`}
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <span className={`font-mono text-[10px] uppercase tracking-widest ${
                            meta ? "text-white/90" : autoStat ? "text-emerald-200" : weekendLocked ? "text-white/30" : isWeekend ? "text-slate-300" : "text-white/50"
                          }`}>
                            {label}
                            <span className="ml-1.5 normal-case tracking-normal" data-testid={`hours-day-date-${e.employee_id}-${d}`}>{addDays(weekStart, d).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                            {meta ? ` · ${meta.short}` : autoStat ? ` · ${REASONS.stat_holiday.short}` : ""}
                            {calStat ? ` (${calStat})` : ""}
                            {isWeekend && !meta ? " · WEEKEND" : ""}
                            {(holiday || autoStat) ? (() => {
                              const ov = holidayOverride[e.employee_id]?.[d];
                              const shown = (ov !== "" && ov != null) ? parseFloat(ov) : statHrs;
                              return shown !== undefined ? ` (${Number(shown || 0).toFixed(2)} hrs)` : "";
                            })() : ""}
                          </span>
                          <div className="flex items-center gap-1">
                            {!reason && !weekendLocked && (
                              <button
                                type="button"
                                onClick={() => splitDay(e.employee_id, d)}
                                data-testid={`hours-mobile-split-${e.employee_id}-${d}`}
                                className="rounded p-1 text-white/40 hover:bg-white/5 hover:text-[#8fa4f0]"
                                aria-label="Split day"
                                title="Split day"
                              >
                                <SplitSquareVertical className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        {weekendLocked ? (
                          <button
                            type="button"
                            onClick={() => toggleWeekendWorked(e.employee_id, d)}
                            data-testid={`hours-mobile-worked-${e.employee_id}-${d}`}
                            className="flex h-9 w-full items-center justify-center rounded border border-dashed border-slate-500/60 bg-slate-500/10 font-mono text-[10px] font-semibold uppercase tracking-widest text-slate-600 hover:border-emerald-400 dark:text-slate-300 hover:bg-emerald-500/10 hover:text-emerald-200"
                          >
                            + Worked
                          </button>
                        ) : reason && reason !== "stat_holiday" ? (
                          <div className={`flex h-9 w-full items-center justify-center rounded border font-mono text-[10px] uppercase tracking-widest ${meta.tone}`}>
                            {meta.short} — 0 hrs
                          </div>
                        ) : effStat ? (
                          <div className="flex items-center gap-2">
                            {calStat && (
                              <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-300" data-testid={`hours-mobile-stat-label-${e.employee_id}-${d}`}>
                                {calStat}
                              </span>
                            )}
                            <input
                              type="text"
                              inputMode="decimal"
                              value={holidayOverride[e.employee_id]?.[d] ?? ""}
                              onChange={(ev) => setHolidayOv(e.employee_id, d, ev.target.value)}
                              placeholder={statHrs !== undefined ? statHrs.toFixed(2) : "0.00"}
                              data-testid={`hours-mobile-stat-hrs-${e.employee_id}-${d}`}
                              className="h-9 w-24 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 text-center font-mono text-sm text-emerald-100 placeholder:text-emerald-300/40 focus:border-emerald-400 focus:outline-none"
                            />
                            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-200">
                              hrs stat @ base
                              {statHrs !== undefined && (holidayOverride[e.employee_id]?.[d] ?? "") === ""
                                ? ` · default ${statHrs.toFixed(2)}`
                                : ""}
                            </span>
                          </div>
                        ) : (
                          days[d].map((entry, ei) => {
                            const fromClock = entry?.source === "clock";
                            const vehicle = entry?.source === "personal_vehicle";
                            const needsLoc = !!entry?.needs_location;
                            const typedLoc = (entry?.typed_location || "").trim();
                            const flash = flashCell === `${e.employee_id}-${d}`;
                            return (
                            <div
                              key={ei}
                              data-row-source={entry?.source || undefined}
                              className={`mb-1 flex items-center gap-2 rounded px-1 py-0.5 last:mb-0 ${vehicle ? "border border-violet-500/60 bg-violet-500/15" : needsLoc ? (typedLoc ? "border border-orange-500 bg-orange-500/20" : "border border-red-500 bg-red-500/20") : fromClock ? "border border-yellow-500/50 bg-yellow-500/15" : ""} ${flash ? `animate-pulse ring-2 ${needsLoc && typedLoc ? "ring-orange-500" : "ring-red-500"}` : ""}`}
                              title={vehicle ? (entry?.note || "Personal vehicle allowance (+2h)") : needsLoc ? (typedLoc ? `Typed location "${typedLoc}" — pick the matching project` : "No job location — pick one") : fromClock ? "Auto-filled from the time clock" : ""}
                              data-locflag={needsLoc ? "true" : undefined}
                            >
                              <input
                                type="text"
                                inputMode="decimal"
                                value={fmtCellHours(entry?.hours) === "0" ? "" : fmtCellHours(entry?.hours)}
                                onChange={(ev) => updateEntry(e.employee_id, d, ei, "hours", ev.target.value)}
                                data-testid={`hours-mobile-cell-${e.employee_id}-${d}-${ei}`}
                                placeholder="hrs"
                                className={`h-9 w-16 shrink-0 rounded border bg-white/5 px-2 text-center focus:border-[#1a3fd1] focus:outline-none ${fromClock ? "border-yellow-500/60 font-semibold text-yellow-900 dark:text-yellow-200" : "border-white/10 text-white/90"}`}
                              />
                              <div className="flex-1">
                                {needsLoc && typedLoc && (
                                  <p className="mb-0.5 truncate px-0.5 font-mono text-[9px] uppercase tracking-wide text-orange-300" title={typedLoc} data-testid={`typed-loc-mobile-${e.employee_id}-${d}-${ei}`}>{typedLoc}</p>
                                )}
                                {vehicle ? (
                                  <div className="flex h-9 items-center justify-center rounded border border-violet-500/50 bg-violet-500/15 px-2 font-mono text-xs uppercase tracking-wider text-violet-200" data-testid={`hours-mobile-vehicle-label-${e.employee_id}-${d}-${ei}`}>Personal vehicle</div>
                                ) : (
                                <JobPicker
                                  value={entry?.project_id || ""}
                                  onChange={(id) => updateEntry(e.employee_id, d, ei, "project_id", id)}
                                  onCreate={() => updateEntry(e.employee_id, d, ei, "project_id", "__new__")}
                                  pinned={PINNED_PROJECTS}
                                  projects={activeProjects}
                                  placeholder="— job —"
                                  className="h-9 text-xs"
                                  testid={`hours-mobile-project-${e.employee_id}-${d}-${ei}`}
                                />
                                )}
                              </div>
                              {ei > 0 && (
                                <button
                                  type="button"
                                  onClick={() => removeSplit(e.employee_id, d, ei)}
                                  data-testid={`hours-mobile-remove-${e.employee_id}-${d}-${ei}`}
                                  aria-label="Remove split"
                                  className="rounded p-1 text-white/40 hover:bg-white/5 hover:text-red-400"
                                >
                                  <XIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                            );
                          })
                        )}

                        {/* Per-day reason dropdown — replaces the old
                            Away/Holiday icon buttons. Selecting any reason
                            zeroes the day's hours and re-colors the card. */}
                        <div className="mt-2">
                          <select
                            value={reason || (autoStat ? "stat_holiday" : "")}
                            onChange={(ev) => setReason(e.employee_id, d, ev.target.value || null)}
                            data-testid={`hours-mobile-reason-${e.employee_id}-${d}`}
                            className={`h-8 w-full rounded border bg-white/5 px-2 font-mono text-[11px] focus:border-[#1a3fd1] focus:outline-none ${
                              meta ? "border-white/20 text-white" : "border-white/10 text-white/60"
                            }`}
                          >
                            <option value="" className="bg-[#0a0a0a] text-white">— Working —</option>
                            {REASON_KEYS.map((k) => (
                              <option key={k} value={k} className="bg-[#0a0a0a] text-white">{REASONS[k].label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-2 text-xs text-white/60">
                    <button
                      type="button"
                      onClick={() => setDetailsFor(e)}
                      data-testid={`hours-mobile-employee-${e.employee_id}`}
                      className="text-[#8fa4f0] hover:underline"
                    >
                      View employee →
                    </button>
                    <span className="font-mono text-white/80">Total: {totalDisplay}</span>
                  </div>
                </div>
              </details>
            );
          })
        )}
      </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
          Week {weekISO}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={deleteWeek}
            data-testid="hours-delete-week"
            className="inline-flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/5 px-4 py-2 text-sm font-semibold text-red-300 hover:border-red-500/50 hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4" /> Delete week
          </button>
          <button
            type="button"
            onClick={() => saveSheet({ advance: false })}
            disabled={saving}
            data-testid="hours-save"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 hover:border-white/30 hover:text-white"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => saveSheet({ advance: true })}
            disabled={saving}
            data-testid="hours-save-next"
            className="inline-flex items-center gap-2 rounded-md bg-[#1a3fd1] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1a3fd1]/85"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save & Start Next Week"}
          </button>
        </div>
      </div>

      <EmployeeDetailsDialog
        employee={detailsFor}
        onOpenChange={(v) => (v ? null : setDetailsFor(null))}
        onEdit={() => setDetailsFor(null)}
      />

      <Dialog open={!!creatingFor} onOpenChange={(v) => (v ? null : setCreatingFor(null))}>
        <DialogContent className="border-white/10 bg-[#0b0b0f] text-white sm:max-w-md" data-testid="create-project-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" /> Create new job
            </DialogTitle>
            <DialogDescription className="text-white/60">
              This job goes straight into Projects as Active and gets flagged for review until the full details are filled in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="np-name" className="text-xs text-white/50">Job name</Label>
              <Input
                id="np-name"
                data-testid="create-project-name"
                value={newProj.name}
                onChange={(ev) => setNewProj((s) => ({ ...s, name: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="np-loc" className="text-xs text-white/50">Address</Label>
              <Input
                id="np-loc"
                data-testid="create-project-location"
                value={newProj.location}
                onChange={(ev) => setNewProj((s) => ({ ...s, location: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
            <div>
              <Label htmlFor="np-desc" className="text-xs text-white/50">Description</Label>
              <Textarea
                id="np-desc"
                data-testid="create-project-description"
                rows={3}
                value={newProj.description}
                onChange={(ev) => setNewProj((s) => ({ ...s, description: ev.target.value }))}
                className="mt-1 border-white/15 bg-white/5 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatingFor(null)} className="border-white/20 bg-transparent text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button
              onClick={createProject}
              disabled={creatingBusy}
              data-testid="create-project-save"
              className="bg-amber-500 text-black hover:bg-amber-400"
            >
              {creatingBusy ? "Creating…" : "Create job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HoursTab;
